
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { 
  PlusCircle, 
  Edit2, 
  FolderOpen,
  FileText,
  Upload,
  Settings,
} from "lucide-react";
import Toast from "../common/Toast";
import { 
  FeatureSection, 
  StatusSection, 
  QuickActionsSection 
} from "../common/Section";
import { AuthError } from "../common/AuthError";

const PageHeader = ({ 
  icon: Icon, 
  title, 
  subtitle, 
  iconGradient = "from-blue-600 to-indigo-600",
  compact = false
}) => (
  <div className={`text-center ${compact ? 'mb-6' : 'mb-8'}`}>
    <div className={`inline-flex items-center justify-center ${compact ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br ${iconGradient} rounded-xl ${compact ? 'mb-3' : 'mb-4'} shadow-lg`}>
      <Icon className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-white`} />
    </div>
    <h1 className={`${compact ? 'text-2xl' : 'text-3xl'} font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ${compact ? 'mb-2' : 'mb-3'}`}>
      {title}
    </h1>
    <p className={`${compact ? 'text-sm' : 'text-base'} text-slate-600 max-w-xl mx-auto`}>
      {subtitle}
    </p>
  </div>
);

const ProjectManagement = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const token = localStorage.getItem("token");

  const features = [
    {
      id: 1,
      title: "Create New Digital Twin Project",
      description: "Start a new project from scratch with our intuitive project creation wizard and advanced configuration options.",
      path: "new-project",
      icon: PlusCircle,
      gradient: "bg-gradient-to-br from-blue-600 to-indigo-600",
      delay: 0
    },
    {
      id: 2,
      title: "Edit Existing Digital Twin Project",
      description: "Modify and enhance your existing digital twin projects with powerful editing tools and real-time updates.",
      path: "user-projects",
      icon: Edit2,
      gradient: "bg-gradient-to-br from-indigo-600 to-purple-600",
      delay: 100
    },
    {
      id: 3,
      title: "Edit Project By File",
      description: "Import and edit digital twin projects directly from configuration files with advanced file management.",
      path: "project-file",
      icon: FileText,
      gradient: "bg-gradient-to-br from-purple-600 to-blue-600",
      delay: 200
    },
    {
      id: 4,
      title: "Bulk Project Import",
      description: "Efficiently import multiple projects at once with our streamlined bulk import system and validation tools.",
      path: "bulk-project-importer",
      icon: Upload,
      gradient: "bg-gradient-to-br from-emerald-500 to-green-600",
      delay: 300
    },
  ];

  const statusCards = [
    {
      id: 1,
      title: "New Projects",
      subtitle: "Start from scratch",
      gradient: "from-blue-500 to-cyan-500",
      path: "new-project",
      icon: PlusCircle
    },
    {
      id: 2,
      title: "Edit Existing",
      subtitle: "Modify current projects",
      gradient: "from-indigo-500 to-purple-500",
      path: "user-projects",
      icon: Edit2
    },
    {
      id: 3,
      title: "Bulk Import",
      subtitle: "Multiple projects at once",
      gradient: "from-emerald-500 to-green-600",
      path: "bulk-project-importer",
      icon: Upload
    }
  ];

  const quickActions = [
    {
      id: 1,
      title: "Quick New Project",
      path: "new-project",
      icon: PlusCircle,
      variant: "primary"
    },
    {
      id: 2,
      title: "View All Projects",
      path: "user-projects",
      icon: Edit2,
      variant: "secondary"
    }
  ];

  // Event handlers
  const handleFeatureClick = (feature) => {
    setToast({ message: `Navigating to ${feature.title}...`, type: "info" });
    navigate(`/${feature.path}`);
  };

  const handleStatusClick = (card) => {
    setToast({ message: `Opening ${card.title}...`, type: "info" });
    navigate(`/${card.path}`);
  };

  const handleQuickActionClick = (action) => {
    setToast({ message: `Executing ${action.title}...`, type: "success" });
    navigate(`/${action.path}`);
  };

  const handleLogin = () => {
    navigate("/login");
  };

  if (!token) {
    return (
      <MainLayout>
        <AuthError />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          
          <PageHeader
            icon={FolderOpen}
            title="Project Management"
            subtitle="Create, edit, and manage your digital twin projects with our comprehensive suite of management tools"
          />

          <StatusSection
            title="Project Status"
            subtitle="Quick access to project categories"
            icon={Settings}
            gradient="from-indigo-600 to-purple-600"
            data={statusCards}
            onItemClick={handleStatusClick}
            className="mb-8"
          />

          <FeatureSection
            title="Management Features"
            subtitle="Choose from our comprehensive project management tools"
            icon={Settings}
            gradient="from-indigo-600 to-purple-600"
            data={features}
            onItemClick={handleFeatureClick}
            gridCols="grid-cols-1 md:grid-cols-2"
            className="mb-8"
          />
          <QuickActionsSection
            title="Quick Actions"
            subtitle="Fast access to frequently used features"
            icon={PlusCircle}
            gradient="from-blue-600 to-indigo-600"
            data={quickActions}
            onItemClick={handleQuickActionClick}
          />
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ProjectManagement;